import { useEffect, useState, useCallback } from 'react';

/**
 * PWA install helper.
 * - Listens for `beforeinstallprompt` on browsers that support it (Chromium/Android/desktop Edge/Chrome).
 * - Detects mobile / tablet devices via user agent + pointer heuristics.
 * - Detects if the app is already installed (`display-mode: standalone`).
 * - Detects iOS (Safari) where the install prompt is not available — the caller
 *   should show manual instructions instead of a button.
 */
export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [platform, setPlatform] = useState({ isMobile: false, isIOS: false, isAndroid: false, isTablet: false });

  useEffect(() => {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);
    const isTablet = /iPad/i.test(ua) || (isAndroid && !/Mobile/i.test(ua));
    const isMobile = isIOS || isAndroid || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    setPlatform({ isMobile, isIOS, isAndroid, isTablet });

    // Detect already-installed (standalone)
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setInstalled(!!standalone);

    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return { ok: false, reason: 'no-prompt' };
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return { ok: choice.outcome === 'accepted', outcome: choice.outcome };
    } catch (e) {
      return { ok: false, reason: e?.message || 'error' };
    }
  }, [deferredPrompt]);

  return {
    canInstall: !!deferredPrompt,
    installed,
    platform,
    install,
  };
};

export default usePWAInstall;
